import { useState, useEffect } from 'react';
import { Users, Activity, Database, CheckCircle } from 'lucide-react';
import { API_URL } from '../../services/api';

interface AdminStats {
  totalUsers: number;
  totalScrapes: number;
  totalEmailsFetched: number;
  adminUsers: number;
  newUsersThisWeek: number;
}

export function AdminOverview() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');

        
        const statsRes = await fetch(`${API_URL}/api/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const statsData = await statsRes.json();
        if (statsData.success) {
            setStats(statsData.data);
        }
      } catch (error) {
        console.error('Failed to fetch admin data', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAdminData();
  }, []);


  if (isLoading) {
    return <div className="p-8 text-white flex justify-center">Loading platform overview...</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
            Platform Overview
          </h1>
          <p className="text-gray-400 mt-2">At a glance platform statistics</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Total Users" value={stats?.totalUsers || 0} icon={<Users className="w-5 h-5" />} color="blue" />
        <StatCard title="Total Scrapes" value={stats?.totalScrapes || 0} icon={<Activity className="w-5 h-5" />} color="green" />
        <StatCard title="Emails Fetched" value={stats?.totalEmailsFetched || 0} icon={<Database className="w-5 h-5" />} color="purple" />
        <StatCard title="New Users (7d)" value={stats?.newUsersThisWeek || 0} icon={<CheckCircle className="w-5 h-5" />} color="orange" />
      </div>

    </div>
  );
}

const StatCard = ({ title, value, icon, color }: { title: string, value: string | number, icon: any, color: 'blue'|'green'|'purple'|'orange' }) => {
  const colorMap = {
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    green: 'text-green-400 bg-green-500/10 border-green-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    orange: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  };

  return (
    <div className={`rounded-2xl border p-6 flex flex-col justify-between ${colorMap[color]} bg-black/40 backdrop-blur-md`}>
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-gray-300 font-medium">{title}</h3>
        <div className={`p-2 rounded-lg bg-black/40`}>
          {icon}
        </div>
      </div>
      <div>
        <div className="text-3xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      </div>
    </div>
  );
};
